import React from 'react';
import type { CriticalResultsKpiKey } from '../../types/criticalResultsDashboard.types';

export interface CriticalResultsKpiItem {
  key: CriticalResultsKpiKey;
  label: string;
  value: string;
  subText?: string;
  tone?: 'default' | 'warning' | 'danger';
  clickable?: boolean;
}

interface CriticalResultsKpiGridProps {
  items: CriticalResultsKpiItem[];
  activeKey?: CriticalResultsKpiKey | null;
  loading?: boolean;
  onSelect?: (key: CriticalResultsKpiKey) => void;
}

function toneClass(tone: CriticalResultsKpiItem['tone']) {
  switch (tone) {
    case 'warning':
      return 'crd-kpi-card--warning';
    case 'danger':
      return 'crd-kpi-card--danger';
    default:
      return '';
  }
}

export default function CriticalResultsKpiGrid({
  items,
  activeKey,
  loading = false,
  onSelect,
}: Readonly<CriticalResultsKpiGridProps>) {
  return (
    <div
      className={['crd-kpi-grid', loading ? 'crd-kpi-grid--loading' : ''].filter(Boolean).join(' ')}
    >
      {items.map(item => {
        const isActive = activeKey === item.key;
        const isClickable = item.clickable !== false && Boolean(onSelect);

        return (
          <article
            key={item.key}
            className={[
              'crd-kpi-card',
              toneClass(item.tone),
              isActive ? 'crd-kpi-card--active' : '',
              isClickable ? 'crd-kpi-card--clickable' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {isClickable ? (
              <button
                type="button"
                className="crd-kpi-card-button"
                onClick={() => onSelect?.(item.key)}
                aria-pressed={isActive}
              >
                <p className="crd-kpi-label">{item.label}</p>
                <p className="crd-kpi-value">{loading ? '—' : item.value}</p>
                {item.subText ? <p className="crd-kpi-subtext">{item.subText}</p> : null}
              </button>
            ) : (
              <div className="crd-kpi-card-body">
                <p className="crd-kpi-label">{item.label}</p>
                <p className="crd-kpi-value">{loading ? '—' : item.value}</p>
                {item.subText ? <p className="crd-kpi-subtext">{item.subText}</p> : null}
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
}
